import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const FIELD =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-sap-blue focus:border-sap-blue outline-none disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed';
const LABEL = 'block text-sm font-medium text-gray-700 mb-1';

const INTERVAL_OPTIONS = [
  { value: '15m', label: 'Every 15 minutes' },
  { value: '30m', label: 'Every 30 minutes' },
  { value: '1h', label: 'Every hour' },
  { value: '3h', label: 'Every 3 hours' },
  { value: '6h', label: 'Every 6 hours' },
  { value: 'custom', label: 'Custom cron expression' },
];

const SCHEDULE_HINTS = {
  '15m': 'Runs every 15 minutes.',
  '30m': 'Runs every 30 minutes.',
  '1h': 'Runs once per hour at minute 0 (e.g. 09:00, 10:00 UTC).',
  '3h': 'Runs every 3 hours at minute 0 (00:00, 03:00, 06:00… UTC).',
  '6h': 'Runs every 6 hours at minute 0 (00:00, 06:00, 12:00… UTC).',
  custom: 'Uses your cron expression on the server clock (UTC).',
};

function syncLogKindLabel(lineKind) {
  const map = {
    sales_order: 'Sales order',
    purchase_order: 'Purchase order',
    production_order: 'Production order',
    inventory_transfer: 'Inventory transfer',
    item: 'BOM item',
    resource: 'BOM resource',
    mixed: 'BOM mixed',
  };
  return map[lineKind] || lineKind || '—';
}

function directionBadge(direction) {
  const inbound = direction !== 'outbound';
  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
        inbound ? 'bg-indigo-100 text-indigo-800' : 'bg-purple-100 text-purple-800'
      }`}
    >
      {inbound ? 'Inbound' : 'Outbound'}
    </span>
  );
}

function syncLogDetailClass(entry) {
  if (entry.status === 'failed') return 'text-red-600';
  if (entry.status === 'skipped') return 'text-amber-700';
  if (entry.status === 'success') return 'text-green-700';
  return 'text-gray-600';
}

const OUTBOUND_LOG_KINDS = new Set(['purchase_order', 'production_order', 'inventory_transfer']);

function isOutboundLogEntry(entry) {
  return OUTBOUND_LOG_KINDS.has(entry.line_kind);
}

function gotSentLabel(entry) {
  const msg = String(entry.error_message || '');
  if (msg.startsWith('Sent to SAP')) return 'Sent';
  if (msg.startsWith('Received from SAP') || msg.startsWith('Received BOM from SAP')) return 'Got';
  if (msg.startsWith('Inbound run complete') || msg.startsWith('Outbound run complete')) return 'Summary';
  if (isOutboundLogEntry(entry)) return 'Sent';
  if (entry.line_kind === 'sales_order' || entry.line_kind === 'item' || entry.line_kind === 'resource') {
    return 'Got';
  }
  return '—';
}

function gotSentBadge(label) {
  const map = {
    Got: 'bg-indigo-100 text-indigo-800',
    Sent: 'bg-purple-100 text-purple-800',
    Summary: 'bg-gray-100 text-gray-700',
  };
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${map[label] || 'bg-gray-100 text-gray-600'}`}>
      {label}
    </span>
  );
}

function filterRunLogs(logs, filter) {
  if (filter === 'all') return logs;
  if (filter === 'got') return logs.filter((l) => gotSentLabel(l) === 'Got');
  if (filter === 'sent') return logs.filter((l) => gotSentLabel(l) === 'Sent');
  return logs;
}

function countRunLogsByDirection(logs) {
  let got = 0;
  let sent = 0;
  for (const l of logs) {
    const label = gotSentLabel(l);
    if (label === 'Got') got += 1;
    else if (label === 'Sent') sent += 1;
  }
  return { got, sent };
}

function SapMessageModal({ open, type, title, message, onClose }) {
  if (!open) return null;
  const isSuccess = type === 'success';
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 backdrop-blur-sm p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sap-modal-title"
      >
        <div className={`px-6 py-5 ${isSuccess ? 'bg-green-50 border-b border-green-100' : 'bg-red-50 border-b border-red-100'}`}>
          <div className="flex items-start gap-3">
            <span
              className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${
                isSuccess ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
              }`}
              aria-hidden
            >
              {isSuccess ? '✓' : '!'}
            </span>
            <div>
              <h2 id="sap-modal-title" className="text-base font-bold text-gray-900">
                {title}
              </h2>
              <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{message}</p>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 flex justify-end bg-white">
          <button
            type="button"
            onClick={onClose}
            className={`px-5 py-2 text-sm font-medium rounded-md text-white ${
              isSuccess ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * What SAP has actually given this document.
 *
 * A draft's DocNum is the number SAP would issue next, not one it has set aside.
 * Two drafts raised minutes apart are told the same number and it may never become
 * a document — 262705201 was shown here and does not exist in SAP at all. Printing
 * it as "DocNum" claimed an identity the document does not yet have, which is why
 * ours and SAP's appeared not to match.
 */
function SapDocCell({ row }) {
  if (row.sap_number_state === 'final' && row.sap_final_doc_num != null) {
    return <span className="text-gray-700">DocNum {row.sap_final_doc_num}</span>;
  }
  if (row.sap_number_state === 'provisional') {
    return (
      <span
        className="text-gray-500"
        title={'Waiting for approval in SAP. SAP assigns the real number when the draft '
          + 'is approved and added'
          + (row.sap_doc_num != null ? `; ${row.sap_doc_num} is only the number it would issue next` : '')
          + '.'}
      >
        Awaiting approval
        {row.sap_draft_entry != null && (
          <span className="text-gray-400"> · draft {row.sap_draft_entry}</span>
        )}
      </span>
    );
  }
  if (row.sap_doc_num != null) return <span className="text-gray-700">DocNum {row.sap_doc_num}</span>;
  if (row.sap_doc_entry != null) return <span className="text-gray-700">DocEntry {row.sap_doc_entry}</span>;
  return <span className="text-gray-400">—</span>;
}

/**
 * One queue table, used by every document tab.
 *
 * "Sent by" had to be added twice because the two tables were copies of each other,
 * so a third copy for inventory transfers would be a third place to forget.
 */
function OutboxTab({
  heading, blurb, docLabel, emptyText, rows,
  openLabel, openPath, onRefresh, onRetry, navigate, queueState,
}) {
  return (
    <div className="p-5 space-y-3">
      <div className="flex justify-between items-start gap-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">{heading}</h3>
          <p className="text-xs text-gray-500 mt-0.5 max-w-3xl">{blurb}</p>
        </div>
        <button type="button" onClick={onRefresh} className="text-xs text-sap-blue hover:underline shrink-0">
          Refresh now
        </button>
      </div>

      {queueState && (rows.some((r) => r.status === 'pending') || queueState.capped) && (
        <OutboundQueueState q={queueState} />
      )}

      <div className="overflow-x-auto border rounded-md">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-2 py-2 text-left">{docLabel}</th>
              <th className="px-2 py-2 text-left">Trigger</th>
              <th className="px-2 py-2 text-left">Sent by</th>
              <th className="px-2 py-2 text-left">Queue Status</th>
              <th className="px-2 py-2 text-left">Queued</th>
              <th className="px-2 py-2 text-left">SAP Doc</th>
              <th className="px-2 py-2 text-left">Processed</th>
              <th className="px-2 py-2 text-left">Error</th>
              <th className="px-2 py-2 text-left">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-gray-400">{emptyText}</td>
              </tr>
            )}
            {rows.map((o) => (
              <tr key={o.outbox_id} className="border-t">
                <td className="px-2 py-1 font-mono">{o.local_doc_number || o.local_id}</td>
                <td className="px-2 py-1 text-gray-600">{o.trigger_action}</td>
                <td className="px-2 py-1 text-gray-600"><SentByCell row={o} /></td>
                <td className="px-2 py-1">{queueBadge(o.status)}</td>
                <td className="px-2 py-1 text-gray-600 whitespace-nowrap">
                  {o.created_at ? (
                    <>
                      {new Date(o.created_at).toLocaleString()}
                      {o.status === 'pending' && (
                        <span className="ml-1 text-amber-700" title="How long this document has been waiting to go to SAP">
                          ({waitingFor(o.created_at)})
                        </span>
                      )}
                    </>
                  ) : '—'}
                </td>
                <td className="px-2 py-1"><SapDocCell row={o} /></td>
                <td className="px-2 py-1 text-gray-600">{o.processed_at ? new Date(o.processed_at).toLocaleString() : '—'}</td>
                <td className="px-2 py-1 text-gray-600 truncate max-w-[280px]" title={o.error_message || undefined}>{o.error_message || '—'}</td>
                <td className="px-2 py-1">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => navigate(openPath(o))}
                      className="text-[10px] px-2 py-0.5 border border-sap-border rounded hover:bg-gray-50 whitespace-nowrap"
                    >
                      {openLabel}
                    </button>
                    {o.status === 'failed' && (
                      <button
                        type="button"
                        onClick={() => onRetry(o.outbox_id)}
                        className="text-[10px] px-2 py-0.5 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                      >
                        Retry
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * What the outbound queue is doing, and why it might not be moving.
 *
 * There is a cap of 120 writes an hour. It is a sensible limit, but it was silent:
 * when it filled, documents simply stopped going out and every screen looked normal.
 * "Nothing is moving" and "nothing is wrong" are different states and the UI owes
 * the reader the difference.
 */
function OutboundQueueState({ q }) {
  const waiting = (q.pending || 0) + (q.processing || 0);
  if (!waiting && !q.capped) {
    return (
      <div className="mb-3 px-3 py-2 rounded-md text-xs bg-gray-50 border text-gray-600">
        Outbound queue is empty — {q.sent_this_hour} of {q.max_per_hour} writes used this hour.
      </div>
    );
  }
  const resumes = q.resumes_at
    ? new Date(new Date(q.resumes_at).getTime() + 60 * 60 * 1000)
    : null;
  return (
    <div className={`mb-3 px-3 py-2 rounded-md text-xs border ${
      q.capped ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-blue-50 border-blue-200 text-blue-900'
    }`}>
      <strong>{waiting}</strong> document{waiting === 1 ? '' : 's'} waiting to go to SAP
      {q.oldest_pending && (
        <> · oldest queued {new Date(q.oldest_pending).toLocaleString()}</>
      )}
      {' · '}
      {q.sent_this_hour} of {q.max_per_hour} writes used this hour.
      {q.capped && (
        <>
          {' '}<strong>The hourly cap is full, so the queue is paused</strong> — not stuck.
          {resumes && <> It starts moving again from about {resumes.toLocaleTimeString()}.</>}
        </>
      )}
    </div>
  );
}

/**
 * What the queue has actually done with a document.
 *
 * These rows were being rendered through statusBadge, which speaks the vocabulary of
 * sync runs — running, success, partial, failed. Anything that was not sent or failed
 * fell through to "running", so a document sitting untouched in the queue was
 * labelled as work in progress. A hundred and twelve of them read "running" for over
 * half an hour while nothing was running at all.
 */
function queueBadge(status) {
  const map = {
    pending:    ['Queued',  'bg-amber-100 text-amber-800'],
    processing: ['Sending', 'bg-blue-100 text-blue-800'],
    sent:       ['Sent',    'bg-green-100 text-green-800'],
    failed:     ['Failed',  'bg-red-100 text-red-800'],
    skipped:    ['Skipped', 'bg-gray-100 text-gray-700'],
  };
  const [label, cls] = map[status] || [status || '—', 'bg-gray-100 text-gray-700'];
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{label}</span>;
}

/** "3h 12m" — how long a document has been waiting, which is the thing being asked. */
function waitingFor(since) {
  if (!since) return null;
  const mins = Math.max(0, Math.round((Date.now() - new Date(since).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  return `${h}h ${mins % 60}m`;
}

/**
 * Who queued this document.
 *
 * Name and address together, because neither alone is enough here. The two halves of
 * the system record different things - a purchase order stores the address, an MRP
 * transfer stores the name - and five purchasing mailboxes are shared by two people
 * each, so a name without the address hides which mailbox it came from and an address
 * without the name says little.
 *
 * Where the address belongs to two accounts and the push predates us recording the
 * user id, the person is genuinely unknown. It says so rather than choosing one.
 */
function SentByCell({ row }) {
  const name = row.requested_by_name;
  const email = row.requested_by_email;
  const shared = !!Number(row.requested_by_shared);

  if (!name && !email && !row.requested_by) {
    return <span className="text-gray-400" title="No user was recorded against this push">—</span>;
  }

  if (!name && shared) {
    return (
      <div className="leading-tight">
        <div className="text-gray-700">{row.requested_by}</div>
        <div className="text-2xs text-amber-700" title="Two accounts share this mailbox, and this push was queued before the person was recorded">
          shared mailbox — person not recorded
        </div>
      </div>
    );
  }

  return (
    <div className="leading-tight">
      <div className="text-gray-700">{name || row.requested_by}</div>
      {email && email !== name && (
        <div className="text-2xs text-gray-400">{email}</div>
      )}
    </div>
  );
}

function statusBadge(status) {
  const map = {
    running: 'bg-blue-100 text-blue-800',
    success: 'bg-green-100 text-green-800',
    partial: 'bg-amber-100 text-amber-800',
    failed: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
}

/**
 * What started a run. Several jobs write to the same run history — the scheduled sync,
 * the live-update worker, manual syncs — and without this a worker pass that applied a
 * few SAP events looks like a full sync that did nothing.
 */
function triggerBadge(trigger) {
  const labels = {
    cron: ['Scheduled', 'bg-blue-50 text-blue-700 border-blue-200'],
    webhook: ['Live update', 'bg-gray-100 text-gray-600 border-gray-200'],
    manual: ['Manual', 'bg-amber-50 text-amber-700 border-amber-200'],
  };
  const [text, cls] = labels[trigger] || [trigger || '—', 'bg-gray-50 text-gray-500 border-gray-200'];
  return <span className={`inline-block px-1.5 py-0.5 rounded border text-2xs font-medium ${cls}`}>{text}</span>;
}

function formatDuration(sec) {
  if (sec == null) return '—';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}

export default function SapB1Settings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role_name === 'Super Admin';

  const [tab, setTab] = useState('Configuration');
  // Inbound and outbound answer different questions — "is SAP's data reaching us"
  // versus "did our document reach SAP" — and were stacked in one list.
  const [logView, setLogView] = useState('Inbound');
  const [form, setForm] = useState({
    service_layer_url: '',
    company_db: '',
    username: '',
    password: '',
    sync_interval_preset: '6h',
    sync_cron_expression: '',
    tls_reject_unauthorized: false,
    write_enabled: false,
    write_po: true,
    write_inventory_transfer: true,
  });
  const [resolvedCron, setResolvedCron] = useState('');
  const [hasStoredPassword, setHasStoredPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [flash, setFlash] = useState(null);
  const [modal, setModal] = useState({ open: false, type: 'success', title: '', message: '' });
  const [runs, setRuns] = useState([]);
  // Off by default: the live-update worker records a pass every few seconds on a working
  // day, which would bury the scheduled syncs this table is mostly used to check.
  const [showLiveUpdates, setShowLiveUpdates] = useState(false);
  const [expandedRun, setExpandedRun] = useState(null);
  const [runLogs, setRunLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logFilter, setLogFilter] = useState('all');
  const [syncEnabled, setSyncEnabled] = useState(true);
  const [syncRunning, setSyncRunning] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [sapWriteEnabled, setSapWriteEnabled] = useState(false);
  const [outbox, setOutbox] = useState([]);
  const [poOutbox, setPoOutbox] = useState([]);
  const [itOutbox, setItOutbox] = useState([]);
  const [syncLogsError, setSyncLogsError] = useState(null);
  const [syncDiag, setSyncDiag] = useState(null);

  const showFlash = (msg, type = 'success') => {
    setFlash({ msg, type });
    setTimeout(() => setFlash(null), 6000);
  };

  const showModal = (type, title, message) => {
    setModal({ open: true, type, title, message });
  };

  const closeModal = () => setModal((m) => ({ ...m, open: false }));

  useEffect(() => {
    if (!modal.open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modal.open]);

  /** Status/cron only — does not overwrite credentials the user is typing. */
  const refreshSapStatus = useCallback(async () => {
    try {
      const data = await api.get('/settings/sap');
      setResolvedCron(data.resolved_cron || '');
      setHasStoredPassword(!!data.has_password);
      setSyncEnabled(data.sync_enabled !== false);
      setSyncRunning(!!data.sync_running);
      setSapWriteEnabled(!!data.sap_write_enabled);
    } catch {
      /* ignore background poll errors */
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const data = await api.get('/settings/sap');
      setForm((prev) => ({
        ...prev,
        service_layer_url: data.service_layer_url || '',
        company_db: data.company_db || '',
        username: data.username || '',
        password: data.has_password ? '••••••••' : '',
        sync_interval_preset: data.sync_interval_preset || '6h',
        sync_cron_expression: data.sync_cron_expression || '',
        tls_reject_unauthorized: !!data.tls_reject_unauthorized,
        write_enabled: !!data.write_enabled,
        write_po: data.write_po !== false,
        write_inventory_transfer: data.write_inventory_transfer !== false,
      }));
      setResolvedCron(data.resolved_cron || '');
      setHasStoredPassword(!!data.has_password);
      setSyncEnabled(data.sync_enabled !== false);
      setSyncRunning(!!data.sync_running);
      setSapWriteEnabled(!!data.sap_write_enabled);
    } catch (e) {
      showFlash(e.message || 'Failed to load SAP settings', 'error');
    }
  }, []);

  const loadRuns = useCallback(async () => {
    try {
      // Filtered on the server: only the latest 50 runs come back, so hiding worker
      // passes here in the browser would leave the table close to empty.
      const data = await api.get(`/settings/sap/sync-runs?limit=50${showLiveUpdates ? '' : '&exclude=webhook'}`);
      setRuns(Array.isArray(data) ? data : []);
      setSyncLogsError(null);
    } catch (e) {
      setRuns([]);
      setSyncLogsError(e.message || 'Could not load sync runs');
    }
  }, [showLiveUpdates]);

  // Each tab asks for its own kind. Asking once for the newest 30 of everything and
  // sorting in the browser is what emptied the PO Approvals tab: one afternoon of
  // inventory transfers filled the window and pushed every purchase order out of it.
  const loadOutbox = useCallback(async () => {
    const get = async (qs) => {
      try {
        const data = await api.get(`/settings/sap/outbox?${qs}`);
        return Array.isArray(data) ? data : [];
      } catch {
        return [];
      }
    };
    const [mixed, pos, its] = await Promise.all([
      get('limit=30'),
      get('limit=30&entity_type=purchase_order'),
      get('limit=30&entity_type=inventory_transfer'),
    ]);
    setOutbox(mixed);
    setPoOutbox(pos);
    setItOutbox(its);
  }, []);

  const loadSyncDiag = useCallback(async () => {
    try {
      const data = await api.get('/settings/sap/diagnostics');
      setSyncDiag(data || null);
    } catch {
      setSyncDiag(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadConfig();
      await loadRuns();
      await loadOutbox();
      await loadSyncDiag();
      setLoading(false);
    })();
  }, [loadConfig, loadRuns, loadOutbox, loadSyncDiag]);

  const hasRunningSync = runs.some((r) => r.status === 'running') || syncRunning;
  // directionBadge treats anything that is not 'outbound' as inbound. Filtering by
  // the same rule keeps a row's badge and the tab it appears under in agreement.
  const visibleRuns = runs.filter((r) =>
    (logView === 'Outbound') === (r.sync_direction === 'outbound'));

  useEffect(() => {
    const tabNeedsPolling = tab === 'Sync Logs' || tab === 'PO Approvals'
      || tab === 'Inventory Transfers';
    if (!hasRunningSync && !tabNeedsPolling) return undefined;
    const id = setInterval(() => {
      refreshSapStatus();
      loadRuns();
      if (tabNeedsPolling) {
        loadOutbox();
        loadSyncDiag();
      }
    }, hasRunningSync ? 5000 : 30000);
    return () => clearInterval(id);
  }, [hasRunningSync, tab, refreshSapStatus, loadRuns, loadOutbox, loadSyncDiag]);

  useEffect(() => {
    if (tab !== 'Sync Logs') return undefined;
    const ms = hasRunningSync ? 5000 : 30000;
    const id = setInterval(() => {
      loadRuns();
      if (expandedRun) {
        api.get(`/settings/sap/sync-runs/${expandedRun}/logs`).then((data) => {
          setRunLogs(Array.isArray(data) ? data : []);
        }).catch(() => {});
      }
    }, ms);
    return () => clearInterval(id);
  }, [tab, expandedRun, loadRuns, hasRunningSync]);

  const loadRunLogs = async (runId) => {
    setLogsLoading(true);
    try {
      const data = await api.get(`/settings/sap/sync-runs/${runId}/logs`);
      setRunLogs(Array.isArray(data) ? data : []);
    } catch {
      setRunLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const toggleRun = async (runId) => {
    if (expandedRun === runId) {
      setExpandedRun(null);
      setRunLogs([]);
      setLogFilter('all');
      return;
    }
    setExpandedRun(runId);
    setLogFilter('all');
    await loadRunLogs(runId);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await api.put('/settings/sap', form);
      setForm((prev) => ({
        ...prev,
        service_layer_url: data.service_layer_url ?? prev.service_layer_url,
        company_db: data.company_db ?? prev.company_db,
        username: data.username ?? prev.username,
        password: data.has_password ? '••••••••' : '',
        sync_interval_preset: data.sync_interval_preset || prev.sync_interval_preset,
        sync_cron_expression: data.sync_cron_expression ?? prev.sync_cron_expression,
        tls_reject_unauthorized: !!data.tls_reject_unauthorized,
        write_enabled: !!data.write_enabled,
        write_po: data.write_po !== false,
        write_inventory_transfer: data.write_inventory_transfer !== false,
      }));
      setResolvedCron(data.resolved_cron || '');
      setHasStoredPassword(!!data.has_password);
      setSapWriteEnabled(!!data.sap_write_enabled);
      showFlash('SAP settings saved. Cron schedule updated.');
    } catch (e) {
      showFlash(e.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    const placeholder = '••••••••';
    const passwordOk =
      (form.password && form.password !== placeholder) || hasStoredPassword;
    if (!form.service_layer_url?.trim()) {
      showFlash('Service Layer URL is required.', 'error');
      return;
    }
    if (!form.company_db?.trim()) {
      showFlash('Company database is required.', 'error');
      return;
    }
    if (!form.username?.trim()) {
      showFlash('Username is required.', 'error');
      return;
    }
    if (!passwordOk) {
      showFlash(
        'Password is required. Type your SAP password, or Save settings first if it is already stored.',
        'error'
      );
      return;
    }

    setTesting(true);
    try {
      await api.post('/settings/sap/test-connection', form);
      showModal(
        'success',
        'SAP connection successful',
        'Login to the SAP Business One Service Layer succeeded. You can Save these settings and use Run sync now or wait for the scheduled job.'
      );
    } catch (e) {
      console.error('[SAP test-connection]', e.message);
      showModal(
        'error',
        'SAP connection failed',
        e.message || 'Could not connect. Check URL, company database, username, password, and network access to SAP.'
      );
    } finally {
      setTesting(false);
    }
  };

  const handleRunSync = async () => {
    if (!syncEnabled) {
      showFlash('Sync is paused. Click Resume first.', 'error');
      return;
    }
    if (!form.service_layer_url?.trim() || !form.company_db?.trim() || !form.username?.trim()) {
      showFlash('Enter Service Layer URL, company DB, and username, then click Save before Run sync now.', 'error');
      return;
    }
    if (!hasStoredPassword && (!form.password || form.password === '••••••••')) {
      showFlash('Enter and Save your SAP password before Run sync now.', 'error');
      return;
    }
    setSyncing(true);
    try {
      const data = await api.post('/settings/sap/run-sync', {});
      setSyncRunning(true);
      showFlash(`Sync started (run #${data.run_id}). Watch Sync Logs tab.`);
      setTab('Sync Logs');
      await loadRuns();
      if (data.run_id) {
        setExpandedRun(data.run_id);
        await loadRunLogs(data.run_id);
      }
    } catch (e) {
      showFlash(e.message || 'Could not start sync', 'error');
    } finally {
      setSyncing(false);
    }
  };

  const handlePause = async () => {
    setPausing(true);
    try {
      await api.post('/settings/sap/pause', {});
      await refreshSapStatus();
      showModal(
        'success',
        'Sync paused',
        'Scheduled sync is stopped. If a sync is running, it will pause between items (no new SAP API calls) until you Resume. No imported data is deleted.'
      );
    } catch (e) {
      showFlash(e.message || 'Could not pause', 'error');
    } finally {
      setPausing(false);
    }
  };

  const handleResume = async () => {
    setResuming(true);
    try {
      await api.post('/settings/sap/resume', {});
      await refreshSapStatus();
      showModal('success', 'Sync resumed', 'Scheduled sync is active again. Any paused in-flight sync will continue on the next item.');
    } catch (e) {
      showFlash(e.message || 'Could not resume', 'error');
    } finally {
      setResuming(false);
    }
  };

  const handleCancelSync = async () => {
    if (!window.confirm('Stop the sync that is running right now? Imported BOMs stay in the database.')) {
      return;
    }
    setCancelling(true);
    try {
      await api.post('/settings/sap/cancel-sync', {});
      setSyncRunning(false);
      await loadRuns();
      showModal(
        'success',
        'Sync cancelled',
        'The sync process was stopped. Your SAP settings, credentials, and any data already imported are unchanged.'
      );
    } catch (e) {
      showFlash(e.message || 'Nothing to cancel or cancel failed', 'error');
    } finally {
      setCancelling(false);
      await refreshSapStatus();
      await loadRuns();
    }
  };

  const handleDisconnect = async () => {
    if (
      !window.confirm(
        'Remove the saved SAP connection (URL, company DB, username, password)?\n\nScheduled sync will stop. Imported BOMs and sync logs are NOT deleted.'
      )
    ) {
      return;
    }
    setDisconnecting(true);
    try {
      const data = await api.post('/settings/sap/disconnect', {});
      setForm({
        service_layer_url: '',
        company_db: '',
        username: '',
        password: '',
        sync_interval_preset: data.sync_interval_preset || '6h',
        sync_cron_expression: data.sync_cron_expression || '',
        tls_reject_unauthorized: false,
      });
      setHasStoredPassword(false);
      setSyncEnabled(false);
      setSyncRunning(false);
      setResolvedCron(data.resolved_cron || '');
      showModal(
        'success',
        'SAP disconnected',
        'Saved credentials were cleared and the schedule was stopped. You can enter new SAP details and Save when ready. Imported BOM data was not deleted.'
      );
    } catch (e) {
      showFlash(e.message || 'Disconnect failed', 'error');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleRetryOutbox = async (outboxId) => {
    try {
      await api.post(`/settings/sap/outbox/${outboxId}/retry`);
      showFlash('Retry queued successfully');
      loadOutbox();
    } catch (e) {
      showFlash(e.message || 'Failed to retry sync', 'error');
    }
  };

  const handleRetryInboundLog = async (logId) => {
    try {
      showFlash('Retrying sync for this item...', 'info');
      await api.post(`/settings/sap/sync-runs/logs/${logId}/retry`);
      showFlash('Retry completed successfully');
      if (expandedRun) {
        await loadRunLogs(expandedRun);
      }
      loadRuns();
    } catch (e) {
      showFlash(e.message || 'Failed to retry sync', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-6 w-6 border-2 border-sap-blue border-t-transparent rounded-full" />
      </div>
    );
  }

  const scheduleHint = SCHEDULE_HINTS[form.sync_interval_preset] || '';

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <SapMessageModal
        open={modal.open}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onClose={closeModal}
      />
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">SAP Integration</h1>
        <p className="text-sm text-gray-500 mt-1">
          <strong>Inbound</strong> (SAP → Anvenssa): today&apos;s sales orders + BOMs on schedule.{' '}
          <strong>Outbound</strong> (Anvenssa → SAP): one document per action — purchase order when status becomes{' '}
          <strong>Open</strong> (Confirm Order, for SAP approval), inventory transfer on Post/Open. Production orders stay
          in Anvenssa only. All runs appear in Sync Logs.
        </p>
        {!sapWriteEnabled && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-2">
            Outbound sync is <strong>off</strong> on this server. Enable <strong>Outbound sync</strong> below and click{' '}
            <strong>Save</strong> (no server restart needed). On Railway you can also set{' '}
            <code className="bg-amber-100 px-0.5">SAP_WRITE_ENABLED=1</code> in service Variables.
          </p>
        )}
        {sapWriteEnabled && (
          <p className="text-xs text-green-800 bg-green-50 border border-green-200 rounded-md px-3 py-2 mt-2">
            Outbound sync is <strong>on</strong>. Purchase orders and inventory transfers will queue to SAP when confirmed/posted.
          </p>
        )}
      </div>

      {flash && (
        <div
          className={`mb-4 px-4 py-3 rounded-md text-sm font-medium ${
            flash.type === 'error'
              ? 'bg-red-50 text-red-700 border border-red-200'
              : 'bg-green-50 text-green-700 border border-green-200'
          }`}
        >
          {flash.msg}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-200">
          {['Configuration', 'Sync Logs', 'PO Approvals', 'Inventory Transfers'].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`px-4 py-2.5 text-sm border-b-2 transition-colors ${
                tab === t
                  ? 'border-sap-blue text-sap-blue font-semibold'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === 'Configuration' && (
          <div className="p-5 space-y-5" autoComplete="off">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className={LABEL}>Service Layer URL</label>
                <input
                  className={FIELD}
                  placeholder="https://host:50000/b1s/v1"
                  value={form.service_layer_url}
                  onChange={(e) => setForm({ ...form, service_layer_url: e.target.value })}
                />
              </div>
              <div>
                <label className={LABEL}>Company database</label>
                <input
                  className={FIELD}
                  value={form.company_db}
                  onChange={(e) => setForm({ ...form, company_db: e.target.value })}
                />
              </div>
              <div>
                <label className={LABEL}>Username</label>
                <input
                  className={FIELD}
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                />
              </div>
              <div className="md:col-span-2">
                <label className={LABEL}>Password</label>
                <input
                  type="password"
                  className={FIELD}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h2 className="text-sm font-semibold text-gray-800 mb-3">Sync schedule</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>Interval</label>
                  <select
                    className={FIELD}
                    disabled={!isSuperAdmin}
                    value={form.sync_interval_preset}
                    onChange={(e) => setForm({ ...form, sync_interval_preset: e.target.value })}
                  >
                    {INTERVAL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                {form.sync_interval_preset === 'custom' && (
                  <div>
                    <label className={LABEL}>Cron expression (UTC)</label>
                    <input
                      className={FIELD}
                      disabled={!isSuperAdmin}
                      placeholder="0 * * * *"
                      value={form.sync_cron_expression}
                      onChange={(e) => setForm({ ...form, sync_cron_expression: e.target.value })}
                    />
                  </div>
                )}
              </div>
              {resolvedCron && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs text-gray-600">
                    <span className="font-medium text-gray-700">Active schedule: </span>
                    <code className="bg-gray-100 px-1 rounded">{resolvedCron}</code>
                    <span className="text-gray-500"> — cron uses the </span>
                    <span className="font-medium">server clock in UTC</span>
                    <span className="text-gray-500">
                      {' '}
                      (not your PC timezone). Example: <code className="bg-gray-100 px-0.5">0 * * * *</code> means
                      at minute 0 of every hour.
                    </span>
                  </p>
                  {scheduleHint && (
                    <p className="text-xs text-gray-500">{scheduleHint}</p>
                  )}
                  <div className="text-xs rounded-md px-3 py-2 border bg-blue-50 border-blue-200 text-blue-900">
                    <span className="font-semibold">How this fits with live updates:</span> changes made in SAP
                    usually reach MRP within a minute through live updates. This scheduled sync is the safety net
                    for anything those miss, so any interval from 15 minutes to several hours works — a longer
                    interval simply means fewer SAP API calls.
                  </div>
                  {form.sync_interval_preset === 'custom' && (
                    <div className="text-xs rounded-md px-3 py-2 border bg-amber-50 border-amber-200 text-amber-900">
                      <span className="font-semibold">Custom cron:</span> Avoid running more often than every 15
                      minutes (e.g. avoid <code className="bg-amber-100 px-0.5">* * * * *</code>) — each run logs in to
                      SAP and pulls orders.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="border-t pt-4">
              <h2 className="text-sm font-semibold text-gray-800 mb-1">Outbound sync (Anvenssa → SAP)</h2>
              <p className="text-xs text-gray-500 mb-3">
                One document per action. Saved here and applied immediately — no API restart required.
              </p>
              <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 space-y-3">
                <label className="flex items-start gap-2 text-sm text-gray-800 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={form.write_enabled}
                    onChange={(e) => setForm({ ...form, write_enabled: e.target.checked })}
                  />
                  <span>
                    <span className="font-medium">Enable outbound sync</span>
                    <span className="text-gray-600"> (master switch — click Save after changing)</span>
                  </span>
                </label>
                <label
                  className={`flex items-start gap-2 text-sm cursor-pointer pl-6 ${
                    form.write_enabled ? 'text-gray-800' : 'text-gray-400'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={form.write_po}
                    disabled={!form.write_enabled}
                    onChange={(e) => setForm({ ...form, write_po: e.target.checked })}
                  />
                  <span>
                    <span className="font-medium">Purchase orders</span>
                    <span className="text-gray-600"> — send to SAP when status becomes Open (Confirm Order)</span>
                  </span>
                </label>
                <label
                  className={`flex items-start gap-2 text-sm cursor-pointer pl-6 ${
                    form.write_enabled ? 'text-gray-800' : 'text-gray-400'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={form.write_inventory_transfer}
                    disabled={!form.write_enabled}
                    onChange={(e) => setForm({ ...form, write_inventory_transfer: e.target.checked })}
                  />
                  <span>
                    <span className="font-medium">Inventory transfers</span>
                    <span className="text-gray-600"> — send to SAP on Post / Open</span>
                  </span>
                </label>
                <p className="text-xs text-gray-500 pl-6">
                  Production orders are <strong>not</strong> sent to SAP (Anvenssa only).
                </p>
              </div>
            </div>

            <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 space-y-2">
              <label className="flex items-start gap-2 text-sm text-gray-800 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={form.tls_reject_unauthorized}
                  onChange={(e) => setForm({ ...form, tls_reject_unauthorized: e.target.checked })}
                />
                <span>
                  <span className="font-medium">Strict TLS certificate validation</span>
                  <span className="text-gray-600"> (enabled = verify HTTPS certificate)</span>
                </span>
              </label>
              <p className="text-xs text-gray-600 leading-relaxed pl-6">
                SAP Service Layer is usually HTTPS. Many on-prem installs use a{' '}
                <strong>self-signed or internal CA certificate</strong>. Leave this{' '}
                <strong>unchecked</strong> (default) so Node can connect despite an untrusted cert. Turn it{' '}
                <strong>on</strong> only when SAP uses a valid public certificate (e.g. proper SSL from a trusted
                authority) — otherwise Test connection fails with certificate / TLS errors.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-2">
              <span
                className={`text-xs px-2 py-1 rounded-full mr-1 ${
                  syncEnabled ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                }`}
              >
                Schedule: {syncEnabled ? 'Active' : 'Paused'}
              </span>
              {hasRunningSync && (
                <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                  Sync running…
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-sap-blue text-white text-sm font-medium rounded-md hover:opacity-90 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={handleTest}
                disabled={testing}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                {testing ? 'Testing…' : 'Test connection'}
              </button>
              {isSuperAdmin && (
                <button
                  type="button"
                  onClick={handleRunSync}
                  disabled={syncing || !syncEnabled}
                  title={!syncEnabled ? 'Resume sync first' : ''}
                  className="px-4 py-2 border border-sap-blue text-sap-blue text-sm font-medium rounded-md hover:bg-sap-blue/5 disabled:opacity-50"
                >
                  {syncing ? 'Starting…' : 'Run sync now'}
                </button>
              )}
              {syncEnabled ? (
                <button
                  type="button"
                  onClick={handlePause}
                  disabled={pausing}
                  className="px-4 py-2 border border-amber-400 text-amber-800 text-sm font-medium rounded-md hover:bg-amber-50 disabled:opacity-50"
                >
                  {pausing ? 'Pausing…' : 'Pause'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleResume}
                  disabled={resuming}
                  className="px-4 py-2 border border-green-600 text-green-800 text-sm font-medium rounded-md hover:bg-green-50 disabled:opacity-50"
                >
                  {resuming ? 'Resuming…' : 'Resume'}
                </button>
              )}
              <button
                type="button"
                onClick={handleCancelSync}
                disabled={cancelling || !hasRunningSync}
                title={!hasRunningSync ? 'Only available while a sync is running' : 'Stop the current sync run'}
                className="px-4 py-2 border border-red-300 text-red-700 text-sm font-medium rounded-md hover:bg-red-50 disabled:opacity-50"
              >
                {cancelling ? 'Cancelling…' : 'Cancel sync'}
              </button>
              {isSuperAdmin && (
                <button
                  type="button"
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  className="px-4 py-2 border border-gray-400 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-100 disabled:opacity-50"
                >
                  {disconnecting ? 'Disconnecting…' : 'Disconnect SAP'}
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 pt-1">
              <strong>Pause</strong> — stop scheduled sync until Resume.{' '}
              <strong>Cancel sync</strong> — stop a run in progress (only when &quot;Sync running…&quot; shows).{' '}
              {isSuperAdmin && (
                <>
                  <strong>Disconnect SAP</strong> — clear saved credentials and stop schedule; imported BOMs stay in MRP.
                </>
              )}
            </p>
          </div>
        )}

        {tab === 'Sync Logs' && (
          <div className="p-5">
            <div className="flex justify-between items-center mb-3">
              <div>
                <p className="text-xs text-gray-500">
                  {hasRunningSync ? 'Sync in progress — refreshing every 5 seconds' : 'Auto-refreshes every 30 seconds'}
                  {' · '}
                  <span className={sapWriteEnabled ? 'text-green-700' : 'text-amber-700'}>
                    Outbound {sapWriteEnabled ? 'enabled' : 'disabled (env)'}
                  </span>
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Click a run row to expand — see what was <strong className="text-indigo-700">Got</strong> from SAP and what was{' '}
                  <strong className="text-purple-700">Sent</strong> to SAP.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  loadRuns();
                  loadOutbox();
                  if (expandedRun) loadRunLogs(expandedRun);
                }}
                className="text-xs text-sap-blue hover:underline"
              >
                Refresh now
              </button>
            </div>

            <div className="flex gap-1 mb-3 border-b">
              {['Inbound', 'Outbound'].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setLogView(v)}
                  className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors ${
                    logView === v
                      ? (v === 'Inbound'
                        ? 'border-indigo-600 text-indigo-700'
                        : 'border-purple-600 text-purple-700')
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  title={v === 'Inbound'
                    ? 'What SAP sent us: sales orders, BOMs, stock and the live-update passes'
                    : 'What we sent SAP: purchase orders and inventory transfers'}
                >
                  {v === 'Inbound' ? 'Inbound (SAP → Anvenssa)' : 'Outbound (Anvenssa → SAP)'}
                  <span className="ml-1.5 text-gray-400 font-normal">
                    {runs.filter((r) => (v === 'Outbound') === (r.sync_direction === 'outbound')).length}
                  </span>
                </button>
              ))}
            </div>

            {logView === 'Outbound' && syncDiag?.outbound_queue && (
              <OutboundQueueState q={syncDiag.outbound_queue} />
            )}

            {logView === 'Outbound' && outbox.length > 0 && (
              <div className="mb-4 border rounded-md overflow-hidden">
                <div className="px-3 py-2 bg-purple-50 border-b text-xs font-semibold text-purple-900">
                  Recent outbound queue (Anvenssa → SAP)
                </div>
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-2 py-1 text-left">Doc</th>
                      <th className="px-2 py-1 text-left">Type</th>
                      <th className="px-2 py-1 text-left">Trigger</th>
                      {/* Who asked for this. Without it the queue cannot answer the
                          first question anyone has when a push fails, and the client
                          had to be told that documents were not sending themselves. */}
                      <th className="px-2 py-1 text-left">Sent by</th>
                      <th className="px-2 py-1 text-left">Status</th>
                      <th className="px-2 py-1 text-left">SAP / error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outbox.slice(0, 15).map((o) => (
                      <tr key={o.outbox_id} className="border-t">
                        <td className="px-2 py-1 font-mono">{o.local_doc_number || o.local_id}</td>
                        <td className="px-2 py-1">{syncLogKindLabel(o.entity_type)}</td>
                        <td className="px-2 py-1 text-gray-600">{o.trigger_action}</td>
                        <td className="px-2 py-1 text-gray-600"><SentByCell row={o} /></td>
                        <td className="px-2 py-1">{queueBadge(o.status)}</td>
                        <td className="px-2 py-1 text-gray-600">
                          {o.sap_doc_num != null || o.sap_number_state === 'provisional' ? (
                            <SapDocCell row={o} />
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="truncate max-w-[280px]" title={o.error_message}>{o.error_message || '—'}</span>
                              {o.status === 'failed' && (
                                <button
                                  type="button"
                                  onClick={() => handleRetryOutbox(o.outbox_id)}
                                  className="px-2 py-0.5 bg-indigo-600 text-white text-[10px] font-semibold rounded hover:bg-indigo-700 transition-colors shrink-0"
                                >
                                  Retry
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {syncLogsError && (
              <div className="mb-4 px-3 py-2 rounded-md text-xs bg-red-50 border border-red-200 text-red-800">
                Could not load sync history: {syncLogsError}. Hard-refresh the page (Ctrl+F5). If this persists after a
                Railway redeploy, log out and log in again as Super Admin.
              </div>
            )}

            {!syncLogsError && runs.length === 0 && syncDiag && (
              <div className="mb-4 px-3 py-2 rounded-md text-xs bg-blue-50 border border-blue-200 text-blue-900">
                <strong>Server database:</strong>{' '}
                {syncDiag.table_exists
                  ? `${syncDiag.run_count} sync run(s) stored${syncDiag.latest_run_id ? ` (latest #${syncDiag.latest_run_id})` : ''}.`
                  : 'SAP sync tables not created yet — redeploy Railway so migrations run.'}{' '}
                {!syncDiag.sap_configured && ' SAP credentials are not saved on this server — open Configuration, Test connection, Save.'}{' '}
                {syncDiag.run_count > 0 &&
                  ' If the table below is still empty, hard-refresh (Ctrl+F5) to load the latest app bundle.'}
              </div>
            )}

            <label className="flex items-center gap-2 text-xs text-gray-600 mb-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showLiveUpdates}
                onChange={(e) => setShowLiveUpdates(e.target.checked)}
              />
              Show live-update passes (the SAP events worker adds one each time it applies changes from SAP)
            </label>
            <div className="overflow-x-auto border rounded-md">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-2 py-2 text-left">Run</th>
                    <th className="px-2 py-2 text-left">Trigger</th>
                    <th className="px-2 py-2 text-left">Dir</th>
                    <th className="px-2 py-2 text-left">Started</th>
                    <th className="px-2 py-2 text-left">Duration</th>
                    <th className="px-2 py-2 text-right">SOs</th>
                    <th className="px-2 py-2 text-right">SO new</th>
                    <th className="px-2 py-2 text-right">SO upd</th>
                    <th className="px-2 py-2 text-right">BOM+</th>
                    <th className="px-2 py-2 text-right">BOM~</th>
                    <th className="px-2 py-2 text-right">Skip</th>
                    <th className="px-2 py-2 text-right">Fail</th>
                    <th className="px-2 py-2 text-right">Out+</th>
                    <th className="px-2 py-2 text-right">Out✗</th>
                    <th className="px-2 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRuns.length === 0 && (
                    <tr>
                      <td colSpan={15} className="px-3 py-6 text-center text-gray-400">
                        {syncLogsError
                          ? 'Sync history unavailable — see message above'
                          : runs.length > 0
                            ? `No ${logView.toLowerCase()} runs in the latest history.`
                            : 'No sync runs yet. Save SAP credentials on Configuration, then click Run sync now.'}
                      </td>
                    </tr>
                  )}
                  {visibleRuns.map((r) => (
                    <React.Fragment key={r.run_id}>
                      <tr
                        className={`border-t cursor-pointer hover:bg-gray-50 ${
                          expandedRun === r.run_id ? 'bg-sap-blue/5' : ''
                        }`}
                        onClick={() => toggleRun(r.run_id)}
                      >
                        <td className="px-2 py-2 font-mono">#{r.run_id}</td>
                        <td className="px-2 py-2">{triggerBadge(r.trigger_type)}</td>
                        <td className="px-2 py-2">{directionBadge(r.sync_direction)}</td>
                        <td className="px-2 py-2">{new Date(r.started_at).toLocaleString()}</td>
                        <td className="px-2 py-2">{formatDuration(r.duration_seconds)}</td>
                        <td className="px-2 py-2 text-right">{r.sales_orders_found}</td>
                        <td className="px-2 py-2 text-right">{r.sos_created ?? 0}</td>
                        <td className="px-2 py-2 text-right">{r.sos_updated ?? 0}</td>
                        <td className="px-2 py-2 text-right">{r.boms_created}</td>
                        <td className="px-2 py-2 text-right">{r.boms_updated}</td>
                        <td className="px-2 py-2 text-right">{r.boms_skipped}</td>
                        <td className="px-2 py-2 text-right">{r.boms_failed}</td>
                        <td className="px-2 py-2 text-right">{r.outbound_sent ?? 0}</td>
                        <td className="px-2 py-2 text-right">{r.outbound_failed ?? 0}</td>
                        <td className="px-2 py-2">{statusBadge(r.status)}</td>
                      </tr>
                      {expandedRun === r.run_id && (
                        <tr className="border-t bg-gray-50">
                          <td colSpan={15} className="px-3 py-3">
                            {r.error_summary && (
                              <p className="text-red-600 text-xs mb-2">{r.error_summary}</p>
                            )}
                            {(() => {
                              const inbound = r.sync_direction !== 'outbound';
                              const { got, sent } = countRunLogsByDirection(runLogs);
                              const summaryLine = runLogs.find(
                                (l) =>
                                  l.item_code === 'Inbound summary' ||
                                  l.item_code === 'Outbound summary' ||
                                  String(l.error_message || '').startsWith('Inbound run complete') ||
                                  String(l.error_message || '').startsWith('Outbound run complete')
                              );
                              return (
                                <>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                                    {inbound && (
                                      <div className="rounded-md border border-indigo-200 bg-indigo-50/80 px-3 py-2 text-xs text-indigo-900">
                                        <div className="font-semibold mb-1">Got from SAP (inbound)</div>
                                        <div>
                                          {r.sales_orders_found ?? 0} SO(s) · {r.sos_created ?? 0} new · {r.sos_updated ?? 0} updated ·{' '}
                                          {r.boms_created ?? 0} BOM+ · {r.boms_updated ?? 0} BOM~ · {r.boms_skipped ?? 0} skip ·{' '}
                                          {r.boms_failed ?? 0} fail
                                        </div>
                                        {got > 0 && (
                                          <div className="text-indigo-700 mt-1">{got} detail line(s) below</div>
                                        )}
                                      </div>
                                    )}
                                    {!inbound && (
                                      <div className="rounded-md border border-purple-200 bg-purple-50/80 px-3 py-2 text-xs text-purple-900">
                                        <div className="font-semibold mb-1">Sent to SAP (outbound)</div>
                                        <div>
                                          {r.outbound_sent ?? 0} sent · {r.outbound_failed ?? 0} failed · {r.outbound_skipped ?? 0} skipped
                                        </div>
                                      </div>
                                    )}
                                    {summaryLine?.error_message && (
                                      <div
                                        className={`rounded-md border px-3 py-2 text-xs ${
                                          inbound
                                            ? 'sm:col-span-2 border-gray-200 bg-white text-gray-700'
                                            : 'border-gray-200 bg-white text-gray-700'
                                        }`}
                                      >
                                        <span className="font-semibold">Run summary: </span>
                                        {summaryLine.error_message}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap items-center gap-2 mb-2">
                                    <span className="text-[10px] uppercase tracking-wide text-gray-500">Show:</span>
                                    {[
                                      { id: 'all', label: 'All' },
                                      { id: 'got', label: 'Got from SAP' },
                                      { id: 'sent', label: 'Sent to SAP' },
                                    ].map((f) => (
                                      <button
                                        key={f.id}
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setLogFilter(f.id);
                                        }}
                                        className={`px-2 py-0.5 rounded text-xs border ${
                                          logFilter === f.id
                                            ? 'bg-sap-blue text-white border-sap-blue'
                                            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                        }`}
                                      >
                                        {f.label}
                                      </button>
                                    ))}
                                  </div>
                                </>
                              );
                            })()}
                            {logsLoading ? (
                              <p className="text-xs text-gray-400">Loading item log…</p>
                            ) : (
                              <table className="min-w-full text-xs border bg-white rounded">
                                <thead>
                                  <tr className="bg-gray-100">
                                    <th className="px-2 py-1 text-left">Dir</th>
                                    <th className="px-2 py-1 text-left">Got / Sent</th>
                                    <th className="px-2 py-1 text-left">Type</th>
                                    <th className="px-2 py-1 text-left">Item / SO</th>
                                    <th className="px-2 py-1 text-left">Action</th>
                                    <th className="px-2 py-1 text-left">Status</th>
                                    <th className="px-2 py-1 text-left">Details</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filterRunLogs(runLogs, logFilter).length === 0 && (
                                    <tr>
                                      <td colSpan={7} className="px-2 py-2 text-gray-400">
                                        {runLogs.length === 0
                                          ? 'No item-level entries for this run'
                                          : `No ${logFilter === 'got' ? 'inbound (Got)' : logFilter === 'sent' ? 'outbound (Sent)' : ''} entries`}
                                      </td>
                                    </tr>
                                  )}
                                  {filterRunLogs(runLogs, logFilter).map((l) => {
                                    const gs = gotSentLabel(l);
                                    const outbound = isOutboundLogEntry(l);
                                    return (
                                      <tr key={l.log_id} className="border-t">
                                        <td className="px-2 py-1">{directionBadge(outbound ? 'outbound' : 'inbound')}</td>
                                        <td className="px-2 py-1">{gotSentBadge(gs)}</td>
                                        <td className="px-2 py-1 text-gray-600">{syncLogKindLabel(l.line_kind)}</td>
                                        <td className="px-2 py-1 font-mono">{l.item_code}</td>
                                        <td className="px-2 py-1">{l.action}</td>
                                        <td className="px-2 py-1">
                                          <div className="flex items-center gap-2">
                                            <span>{l.status}</span>
                                            {l.status === 'failed' && (l.line_kind === 'sales_order' || l.line_kind === 'item') && (
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleRetryInboundLog(l.log_id);
                                                }}
                                                className="px-1.5 py-0.5 bg-indigo-600 text-white text-[10px] font-semibold rounded hover:bg-indigo-700 transition-colors shrink-0"
                                              >
                                                Retry
                                              </button>
                                            )}
                                          </div>
                                        </td>
                                        <td className={`px-2 py-1 max-w-xl whitespace-pre-wrap break-words ${syncLogDetailClass(l)}`}>
                                          {l.error_message || '—'}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'PO Approvals' && (
          <OutboxTab
            heading="POs sent to SAP for approval"
            blurb="Queue and delivery status for purchase orders sent from Anvenssa to SAP."
            docLabel="PO Number"
            emptyText="No purchase-order approval sends yet."
            rows={poOutbox}
            openLabel="Open PO"
            openPath={(o) => `/purchase-order/${o.local_id}`}
            queueState={syncDiag?.outbound_queue}
            onRefresh={loadOutbox}
            onRetry={handleRetryOutbox}
            navigate={navigate}
          />
        )}

        {tab === 'Inventory Transfers' && (
          <OutboxTab
            heading="Inventory transfers sent to SAP"
            blurb={'Queue and delivery status for stock transfers posted in Anvenssa. A transfer '
              + 'moves our stock as soon as it is posted, so one that never reached SAP is work '
              + 'that exists in neither system — this is where that shows.'}
            docLabel="Transfer No."
            emptyText="No inventory transfer sends yet."
            rows={itOutbox}
            openLabel="Open transfer"
            openPath={(o) => `/inventory/transfer/${o.local_id}`}
            queueState={syncDiag?.outbound_queue}
            onRefresh={loadOutbox}
            onRetry={handleRetryOutbox}
            navigate={navigate}
          />
        )}
      </div>
    </div>
  );
}
